// Shared aggregation + attention-slot / sub-line logic for the Work Orders and
// Operations tabs (design_handoff_wo_ops_list). Kept out of app.js so the two
// tabs can never drift on wording for the same condition — every place that
// needs to say "Parts on order · ETA Jul 30" calls the same function.
//
// Every field read here is one of the ✅ fields from the handoff's data-
// provenance table — nothing here invents a field the app doesn't already have,
// except Expected_Date (Part 3 of the handoff, added to /receiving/<id>).

import { parseDay } from './date-format';
import { techLabel } from './techNames';

export { parseDay };

const DONE_STATUSES = ['Ready to Bill', 'Complete', 'Closed'];
const AVATAR_COLORS = ['#0C2C49', '#1462B8', '#0F8C82', '#123B5E', '#5A6B7B'];

function stableHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

// "Mon DD" (Jul 30), year appended only when the date falls outside the
// current calendar year — same rule the op rows already use (app.js:8441).
export function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const d = parseDay(dateStr);
  if (isNaN(d)) return '';
  const opts = { month: 'short', day: '2-digit' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts);
}

function startOfDay(d) {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function daysBetween(a, b) {
  return Math.round((startOfDay(a) - startOfDay(b)) / 86400000);
}

// Monday 00:00 through Sunday 23:59 of the current week, local time (A2).
export function isThisWeek(dateStr) {
  if (!dateStr) return false;
  const d = parseDay(dateStr);
  if (isNaN(d)) return false;
  const now = new Date();
  const day = now.getDay(); // 0 = Sun
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset));
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 7);
  return d >= monday && d < sunday;
}

// ---------------------------------------------------------------------------
// Tech avatars (Appendix A3) — no avatar-color system exists in the app today.
// getTechId()'s table only covers a handful of names (it's a DockMaster-id
// lookup, not a full roster), so it's used as a stability hint when present
// and a name-hash fallback covers everyone else — still stable per tech.
// ---------------------------------------------------------------------------

export function techAvatar(name, getTechIdFn) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { initials: '?', color: AVATAR_COLORS[4] };
  if (trimmed === 'Yard') return { initials: 'Y', color: '#5A6B7B' };

  const words = trimmed.split(/\s+/).filter(Boolean);
  // From the on-screen name ("Rafael M." → RM), so a saved first name still gets two letters.
  const shown = techLabel(trimmed).split(/\s+/).filter(Boolean);
  const initials = (shown[0]?.[0] || '').toUpperCase() + (shown[1]?.[0] || '').toUpperCase();

  const techId = getTechIdFn ? getTechIdFn(words[0]) : undefined;
  const hashSource = techId != null ? String(techId) : trimmed;
  const color = AVATAR_COLORS[stableHash(hashSource) % AVATAR_COLORS.length];

  return { initials: initials || trimmed[0]?.toUpperCase() || '?', color };
}

export function splitTechs(techsStr) {
  return (techsStr || '').split(', ').map(t => t.trim()).filter(Boolean);
}

// ---------------------------------------------------------------------------
// Part 3 — outstanding /receiving orders for a job
// ---------------------------------------------------------------------------

// receivingData.filter(r => r.Job_ID === jobId && Number(r.Quantity_Remaining) > 0),
// sorted so the earliest non-empty Expected_Date wins; falls back to earliest
// ordered Date when nothing has an ETA.
export function outstandingPartsForJob(jobId, receivingData) {
  const outstanding = (receivingData || []).filter(
    r => r.Job_ID === jobId && Number(r.Quantity_Remaining) > 0
  );
  return outstanding.sort((a, b) => {
    const aEta = a.Expected_Date ? new Date(a.Expected_Date).getTime() : null;
    const bEta = b.Expected_Date ? new Date(b.Expected_Date).getTime() : null;
    if (aEta != null && bEta != null) return aEta - bEta;
    if (aEta != null) return -1;
    if (bEta != null) return 1;
    const aDate = a.Date ? new Date(a.Date).getTime() : Infinity;
    const bDate = b.Date ? new Date(b.Date).getTime() : Infinity;
    return aDate - bDate;
  });
}

// Classifies one outstanding order against today's date: 'future' | 'today' |
// 'overdue' | 'no-eta'. Used by both the attention-slot part-order rule and
// the operation sub-line so wording/color never diverges (Part 3 5-case table).
function classifyPartOrder(order) {
  if (!order.Expected_Date) return { state: 'no-eta', daysLate: 0 };
  const eta = parseDay(order.Expected_Date);
  if (isNaN(eta)) return { state: 'no-eta', daysLate: 0 };
  const diff = daysBetween(eta, new Date());
  if (diff < 0) return { state: 'overdue', daysLate: -diff };
  if (diff === 0) return { state: 'today', daysLate: 0 };
  return { state: 'future', daysLate: 0 };
}

// Builds the { headline, detail, severity } for a set of outstanding orders on
// ONE operation (Operations-tab sub-line) or on a whole work order's pooled
// orders (Work-Orders-tab attention slot) — same 5-case table, different caller.
function describePartOrders(outstanding) {
  if (!outstanding.length) return null;

  const classified = outstanding.map(o => ({ order: o, ...classifyPartOrder(o) }));
  const anyOverdue = classified.some(c => c.state === 'overdue');
  const earliest = classified[0];

  const oneLineFor = (c) => {
    const { order, state, daysLate } = c;
    if (state === 'future') return `${order.Part_Number} · ETA ${formatShortDate(order.Expected_Date)}`;
    if (state === 'today') return `${order.Part_Number} · due today`;
    if (state === 'overdue') return `${order.Part_Number} · expected ${formatShortDate(order.Expected_Date)}, ${daysLate} day${daysLate === 1 ? '' : 's'} late`;
    return `${order.Part_Number} · no ETA · ordered ${formatShortDate(order.Date)}`;
  };

  const sublineFor = (c) => {
    const { state, daysLate } = c;
    if (state === 'future') return `Parts on order · ETA ${formatShortDate(c.order.Expected_Date)}`;
    if (state === 'today') return `Parts on order · due today`;
    if (state === 'overdue') return `Parts on order · ${daysLate} day${daysLate === 1 ? '' : 's'} late`;
    return `Parts on order · no ETA`;
  };

  if (classified.length > 1) {
    return {
      headline: `${classified.length} parts on order`,
      detail: oneLineFor(earliest),
      subline: `${classified.length} parts on order${earliest.state === 'future' ? ` · ETA ${formatShortDate(earliest.order.Expected_Date)}` : ''}`,
      severity: anyOverdue ? 'red' : 'amber',
    };
  }

  const c = earliest;
  return {
    headline: c.state === 'overdue' ? 'Part order overdue' : 'Parts on order',
    detail: oneLineFor(c),
    subline: sublineFor(c),
    severity: c.state === 'overdue' ? 'red' : 'amber',
  };
}

// ---------------------------------------------------------------------------
// Work-order aggregation — "✅ Values derived by simple aggregation" table
// ---------------------------------------------------------------------------

export function aggregateWorkOrder(ops) {
  const activeOps = (ops || []).filter(op => op.Status?.toString() !== 'Declined');

  const totalEstHours = activeOps.reduce((s, op) => s + (parseFloat(op.Estimated_Hours) || 0), 0);
  const loggedHours = activeOps.reduce((s, op) => {
    const labor = op.Labor ? Object.values(op.Labor) : [];
    return s + labor.reduce((ss, l) => ss + (parseFloat(l.Hours) || 0), 0);
  }, 0);
  const inProgressHours = activeOps
    .filter(op => op.Status === 'In Progress')
    .reduce((s, op) => s + (parseFloat(op.Estimated_Hours) || 0), 0);

  const doneOps = activeOps.filter(op => DONE_STATUSES.includes(op.Status?.toString()));
  const doneCount = doneOps.length;
  const totalCount = activeOps.length;

  // Done ops don't need a Schedule_Date/Techs anymore — scheduling something already
  // complete/billable is moot, so excluding DONE_STATUSES here keeps a 100%-done WO from
  // simultaneously flagging an "N operations unscheduled" attention badge (Rule 3 below).
  const unscheduledOps = activeOps.filter(op => !DONE_STATUSES.includes(op.Status?.toString()) && (!op.Schedule_Date || !op.Techs));
  const unscheduledHours = unscheduledOps.reduce((s, op) => s + (parseFloat(op.Estimated_Hours) || 0), 0);

  const blockedCount = activeOps.filter(op =>
    Number(op.Operation_Dependency) > 0 ||
    op.Status === 'Hold' ||
    op.Parts_Ordered === 'Part Ordered'
  ).length;

  const runningOp = activeOps.find(op => op.Status === 'In Progress');
  const scheduledOps = activeOps.filter(op => !DONE_STATUSES.includes(op.Status?.toString()) && op.Status !== 'In Progress' && op.Schedule_Date);

  // Always operation-count-based ("N of M done"), not hours-based. Hours-
  // ratio was tried first (per the original design spec) but real ops that
  // run long routinely pushed logged-hours past estimated-hours, producing
  // nonsense like "900%" — count of done-vs-total operations can't exceed
  // 100% and reads correctly regardless of how estimates track reality.
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
  const pctIsCountBased = true;

  const futureScheduled = activeOps
    .filter(op => op.Schedule_Date && parseDay(op.Schedule_Date) >= startOfDay(new Date()))
    .sort((a, b) => parseDay(a.Schedule_Date) - parseDay(b.Schedule_Date));
  const nextScheduleDate = futureScheduled[0]?.Schedule_Date || null;

  const opsThisWeek = activeOps.filter(op => op.Schedule_Date && isThisWeek(op.Schedule_Date) && !DONE_STATUSES.includes(op.Status?.toString()));

  return {
    activeOps,
    totalEstHours,
    loggedHours,
    inProgressHours,
    doneCount,
    totalCount,
    pct,
    pctIsCountBased,
    unscheduled: { count: unscheduledOps.length, hours: unscheduledHours, ops: unscheduledOps },
    blockedCount,
    runningOps: activeOps.filter(op => op.Status === 'In Progress'),
    scheduledOps,
    doneOps,
    nextScheduleDate,
    opsThisWeekCount: opsThisWeek.length,
    overEstimate: totalEstHours > 0 && loggedHours > totalEstHours ? (loggedHours - totalEstHours) : 0,
  };
}

// ---------------------------------------------------------------------------
// Attention slot — Work Orders tab, 6-rule precedence (README §Attention slot)
// ---------------------------------------------------------------------------

export function resolveAttentionSlot({ ops, receivingData, boatData, rollupStatus }) {
  const agg = aggregateWorkOrder(ops);
  const activeOps = agg.activeOps;

  // Rule 1 — any op on Hold
  const heldOps = activeOps.filter(op => op.Status === 'Hold');
  if (heldOps.length) {
    const latestTime = heldOps.reduce((latest, op) => {
      const entries = op.Labor ? Object.values(op.Labor) : [];
      entries.forEach(l => {
        const t = l.Time_Submitted ? new Date(l.Time_Submitted).getTime() : NaN;
        if (!isNaN(t) && (!latest || t > latest)) latest = t;
      });
      return latest;
    }, null);
    return {
      rule: 'hold',
      modifier: 'hold',
      headline: 'On hold',
      detail: latestTime ? `Last activity ${formatShortDate(new Date(latestTime))}` : '',
    };
  }

  // Rule 2 — any op with an outstanding part order
  const jobIdsWithParts = activeOps.filter(op => op.Parts_Ordered === 'Part Ordered').map(op => op.Job_ID);
  if (jobIdsWithParts.length) {
    const pooled = jobIdsWithParts.flatMap(id => outstandingPartsForJob(id, receivingData));
    const described = describePartOrders(pooled);
    if (described) {
      return {
        rule: 'parts',
        modifier: described.severity === 'red' ? 'overdue' : 'parts',
        headline: described.headline,
        detail: described.detail,
      };
    }
  }

  // Rule 3 — unscheduled ops. Headline alone — no hours detail line (hours
  // estimates are unreliable enough that a "N.0 hrs with nowhere to go"
  // figure reads as more precise/meaningful than it actually is).
  if (agg.unscheduled.count > 0) {
    return {
      rule: 'unscheduled',
      modifier: 'parts',
      headline: `${agg.unscheduled.count} operation${agg.unscheduled.count === 1 ? '' : 's'} unscheduled`,
      detail: '',
    };
  }

  // Rule 4 — rollup status Ready to Bill
  if (rollupStatus === 'Ready to Bill') {
    const detail = agg.overEstimate > 0 ? `${agg.overEstimate.toFixed(1)} hrs over estimate — worth a look` : '';
    return { rule: 'ready', modifier: 'ready', headline: 'Nothing outstanding', detail, showInvoice: true };
  }

  // Rule 5 — waiting on the haul/launch (Winterizing / Spring Commissioning only)
  const category = activeOps[0]?.Category;
  if (category === 'Winterizing' && boatData?.Haul_Date && parseDay(boatData.Haul_Date) >= startOfDay(new Date())) {
    return {
      rule: 'waiting-haul',
      modifier: 'waiting',
      headline: 'Waiting on the haul',
      detail: agg.totalCount > 1
        ? `${formatShortDate(boatData.Haul_Date)} · the other ${agg.totalCount - 1} follow it`
        : formatShortDate(boatData.Haul_Date),
    };
  }
  if (category === 'Spring Commissioning' && boatData?.Launch_Date && parseDay(boatData.Launch_Date) >= startOfDay(new Date())) {
    return {
      rule: 'waiting-launch',
      modifier: 'waiting',
      headline: 'Waiting on the launch',
      detail: agg.totalCount > 1
        ? `${formatShortDate(boatData.Launch_Date)} · the other ${agg.totalCount - 1} follow it`
        : formatShortDate(boatData.Launch_Date),
    };
  }

  // Rule 6 — default
  return {
    rule: 'default',
    modifier: 'default',
    headline: 'Running to plan',
    detail: `${agg.opsThisWeekCount} op${agg.opsThisWeekCount === 1 ? '' : 's'} booked this week · nothing blocked`,
  };
}

// ---------------------------------------------------------------------------
// Operation sub-line — Operations tab, precedence per README §Operation sub-line
// ---------------------------------------------------------------------------

export function resolveOperationSubline(op, receivingData) {
  const category = op.Category || '';

  let clause = '';
  if (op.Status === 'In Progress') {
    const logged = op.Labor ? Object.values(op.Labor).reduce((s, l) => s + (parseFloat(l.Hours) || 0), 0) : 0;
    const est = parseFloat(op.Estimated_Hours) || 0;
    clause = `${logged.toFixed(1)} of ${est.toFixed(1)} hrs logged`;
  } else if (op.Status === 'Hold') {
    // Op-panel rebuild Stage 7c — once a hold carries a reason (new
    // Hold_Reason/Hold_Detail/Hold_Follow_Up_Date fields), surface it here
    // instead of the bare "On hold"; older holds set before that feature
    // shipped won't have these fields, so this falls back cleanly.
    if (op.Hold_Reason) {
      clause = op.Hold_Detail ? `${op.Hold_Reason} — ${op.Hold_Detail}` : op.Hold_Reason;
      if (op.Hold_Follow_Up_Date) {
        const d = new Date(op.Hold_Follow_Up_Date + 'T00:00:00');
        if (!isNaN(d)) clause += ` · follow up ${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
      }
    } else {
      clause = 'On hold';
    }
  // Checked before Parts_Ordered/dependency/schedule below: once an op is done, whether it still
  // has parts on order, a dependency, or a schedule date is moot — those are all pre-done concerns.
  // (This ordering was previously reversed, which made a closed op with a blank Schedule_Date show
  // "Not scheduled" instead of "Closed out" — see the matching fix in operationSeverity() below.)
  } else if (DONE_STATUSES.includes(op.Status?.toString())) {
    clause = op.Complete_Date ? `Closed out ${formatShortDate(op.Complete_Date)}` : 'Closed out';
  } else if (op.Parts_Ordered === 'Part Ordered') {
    const outstanding = outstandingPartsForJob(op.Job_ID, receivingData);
    const described = describePartOrders(outstanding);
    clause = described ? described.subline : 'Parts on order · no ETA';
  } else if (Number(op.Operation_Dependency) > 0) {
    clause = 'Blocked by dependency';
  } else if (!op.Schedule_Date || !op.Techs) {
    clause = 'Not scheduled';
  }

  return clause ? `${category} · ${clause}` : category;
}

// Same severity classification the attention slot uses, exposed standalone so
// the Operations-tab row dot / status pill can color itself without re-deriving
// the sub-line text.
export function operationSeverity(op, receivingData) {
  if (op.Status === 'Hold') return 'hold';
  // Checked before Parts_Ordered/schedule below — same reasoning and same bug this mirrors in
  // resolveOperationSubline() above: a done op with a blank Schedule_Date was rendering as
  // 'unscheduled' (an amber "Needs sched." pill) instead of 'done', most visibly on the Closed tab.
  if (DONE_STATUSES.includes(op.Status?.toString())) return 'done';
  if (op.Parts_Ordered === 'Part Ordered') {
    const outstanding = outstandingPartsForJob(op.Job_ID, receivingData);
    const described = describePartOrders(outstanding);
    return described?.severity === 'red' ? 'overdue' : 'parts';
  }
  if (!op.Schedule_Date || !op.Techs) return 'unscheduled';
  if (op.Status === 'In Progress') return 'in-progress';
  return 'scheduled';
}

// Earliest overdue part-order ETA across a work order's ops — the first
// tie-break in Appendix A9's Urgency sort.
export function earliestOverdueEta(ops, receivingData) {
  let earliest = null;
  const today = startOfDay(new Date());
  (ops || []).forEach(op => {
    if (op.Parts_Ordered !== 'Part Ordered') return;
    outstandingPartsForJob(op.Job_ID, receivingData).forEach(o => {
      if (!o.Expected_Date) return;
      const d = parseDay(o.Expected_Date);
      if (isNaN(d) || d >= today) return;
      if (!earliest || d < earliest) earliest = d;
    });
  });
  return earliest;
}

// ---------------------------------------------------------------------------
// Appendix A9 — Urgency sort tie-breaks (Work Orders tab)
// ---------------------------------------------------------------------------

const PRIORITY_ORDER = ['High', 'Normal', 'Low'];

export function urgencyRank(slotRule) {
  if (slotRule === 'hold' || slotRule === 'parts') return 0;
  if (slotRule === 'unscheduled') return 1;
  return 2;
}

export function compareUrgency(a, b) {
  // a, b: { slot, ops, data }
  const rankDiff = urgencyRank(a.slot.rule) - urgencyRank(b.slot.rule);
  if (rankDiff !== 0) return rankDiff;

  const aOverdueEta = a.earliestOverdueEta;
  const bOverdueEta = b.earliestOverdueEta;
  if (aOverdueEta && bOverdueEta) {
    const diff = new Date(aOverdueEta) - new Date(bOverdueEta);
    if (diff !== 0) return diff;
  } else if (aOverdueEta) {
    return -1;
  } else if (bOverdueEta) {
    return 1;
  }

  const aSched = a.data.Schedule_Date ? new Date(a.data.Schedule_Date).getTime() : Infinity;
  const bSched = b.data.Schedule_Date ? new Date(b.data.Schedule_Date).getTime() : Infinity;
  if (aSched !== bSched) return aSched - bSched;

  const aPri = PRIORITY_ORDER.indexOf(a.data.Priority) === -1 ? PRIORITY_ORDER.length : PRIORITY_ORDER.indexOf(a.data.Priority);
  const bPri = PRIORITY_ORDER.indexOf(b.data.Priority) === -1 ? PRIORITY_ORDER.length : PRIORITY_ORDER.indexOf(b.data.Priority);
  if (aPri !== bPri) return aPri - bPri;

  return String(b.data.Work_Order).localeCompare(String(a.data.Work_Order));
}
